import { supabase } from '../lib/supabaseClient';
import { saveSignup } from './signupService';

const normalizeText = (value) => value?.trim?.() || '';

const splitName = (value) => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

async function syncProfileRecord({ userId, email, firstName, lastName, city, state, country }) {
  if (!userId) {
    throw new Error('No authenticated user found for profile sync.');
  }

  const payload = {
    id: userId,
    email: normalizeText(email).toLowerCase() || null,
    first_name: normalizeText(firstName) || null,
    last_name: normalizeText(lastName) || null,
    city: normalizeText(city) || null,
    state: normalizeText(state) || null,
    country: normalizeText(country) || null,
  };

  const { error } = await supabase
    .from('profiles')
    .upsert([payload], { onConflict: 'id' });

  if (error) throw error;
}

export function getProfileFromUser(user) {
  if (!user) return null;

  const metadata = user.user_metadata || {};
  const parsedName = splitName(metadata.full_name || '');

  return {
    email: user.email || '',
    fullName: metadata.full_name || '',
    firstName: metadata.first_name || parsedName.firstName || '',
    lastName: metadata.last_name || parsedName.lastName || '',
    city: metadata.city || '',
    state: metadata.state || '',
    country: metadata.country || '',
  };
}

export async function signUpWithEmail({ email, password, fullName, firstName, lastName, city, state, country }) {
  const { data, error } = await supabase.auth.signUp({
    email: email.trim().toLowerCase(),
    password,
    options: {
      data: {
        full_name: normalizeText(fullName),
        first_name: normalizeText(firstName),
        last_name: normalizeText(lastName),
        city: normalizeText(city),
        state: normalizeText(state),
        country: normalizeText(country),
      },
    },
  });

  if (error) throw error;
  await saveSignup({ email, firstName, lastName, city, state, country });
  return { data };
}

export async function signInWithEmail({ email, password }) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  });

  if (error) throw error;
  return { data };
}

export async function signOutUser() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function requestPasswordReset(email) {
  const redirectTo = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}`
    : undefined;

  const { error } = await supabase.auth.resetPasswordForEmail(
    email.trim().toLowerCase(),
    redirectTo ? { redirectTo } : undefined
  );

  if (error) throw error;
}

export async function updateUserPassword(password) {
  const { data, error } = await supabase.auth.updateUser({ password });

  if (error) throw error;
  return { data };
}

export async function updateUserProfile({ fullName, firstName, lastName, city, state, country }) {
  const { data, error } = await supabase.auth.updateUser({
    data: {
      full_name: normalizeText(fullName),
      first_name: normalizeText(firstName),
      last_name: normalizeText(lastName),
      city: normalizeText(city),
      state: normalizeText(state),
      country: normalizeText(country),
    },
  });

  if (error) throw error;

  await syncProfileRecord({
    userId: data.user?.id,
    email: data.user?.email,
    firstName,
    lastName,
    city,
    state,
    country,
  });

  return { data };
}

export async function deleteCurrentUserAccount() {
  const { error } = await supabase.rpc('delete_own_account');
  if (error) throw error;
}
